import { FormEvent, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import './wompi-checkout.css'
import { brandFor, digitsOf, formatExpiry, groupDigits, luhn } from './wompiCard'

function cardholderName(value: string) {
  return value.trim().replace(/\s+/g, ' ')
}

function isCardholderName(value: string) {
  const name = cardholderName(value)
  return name.length >= 3 && /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+(?:[ '\-.][A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+)+$/.test(name)
}

type Copy = Record<string, string>

type CheckoutConfig = {
  status: boolean
  redirect?: string | null
  usd_label?: string
  coins?: number
  full_name?: string
  public_key?: string
  tokenize_url?: string
  pay_card_url?: string
  wompi_site_url?: string
  copy?: Copy
  message?: string
}

export function WompiCheckoutPage() {
  const { orderId = '' } = useParams()
  const [params] = useSearchParams()
  const [config, setConfig] = useState<CheckoutConfig | null>(null)
  const [error, setError] = useState('')
  const [number, setNumber] = useState('')
  const [exp, setExp] = useState('')
  const [cvc, setCvc] = useState('')
  const [holder, setHolder] = useState('')
  const [accept, setAccept] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const lang = params.get('lang') || 'es'
    let stop = false
    fetch(`/api/checkout/${encodeURIComponent(orderId)}?lang=${encodeURIComponent(lang)}`)
      .then(async (res) => {
        const body = await res.json()
        if (!res.ok) throw new Error(body.message || 'Pago no encontrado')
        return body as CheckoutConfig
      })
      .then((body) => {
        if (stop) return
        if (body.redirect) {
          window.location.replace(body.redirect)
          return
        }
        setConfig(body)
        if (body.full_name && isCardholderName(body.full_name)) setHolder(body.full_name)
      })
      .catch((err: Error) => {
        if (!stop) setError(err.message || 'Pago no encontrado')
      })
    return () => { stop = true }
  }, [orderId, params])

  const copy = config?.copy || {}
  const brand = brandFor(digitsOf(number))
  const cvcLen = brand?.cvc || 3

  function validate() {
    const next: Record<string, string> = {}
    const pan = digitsOf(number)
    if (!pan) next.number = copy.err_number_empty || ''
    else if (!brand) next.number = pan.length < 6 ? (copy.err_number_incomplete || '') : (copy.err_brand || '')
    else if (!brand.lengths.includes(pan.length)) {
      const longest = Math.max(...brand.lengths)
      next.number = pan.length < longest ? (copy.err_number_incomplete || '') : (copy.err_number_invalid || '')
    } else if (!luhn(pan)) next.number = copy.err_number_invalid || ''

    const expDigits = digitsOf(exp)
    if (!expDigits) next.exp = copy.err_exp_empty || ''
    else if (expDigits.length !== 4) next.exp = copy.err_exp_invalid || ''
    else {
      const month = Number(expDigits.slice(0, 2))
      const year = 2000 + Number(expDigits.slice(2, 4))
      if (month < 1 || month > 12) next.exp = copy.err_exp_month || ''
      else if (new Date(year, month, 0, 23, 59, 59) < new Date()) next.exp = copy.err_exp_past || ''
    }

    const cvcDigits = digitsOf(cvc)
    if (!cvcDigits) next.cvc = copy.err_cvc_empty || ''
    else if (cvcDigits.length !== cvcLen) next.cvc = (copy.err_cvc_length || '').replace(':n', String(cvcLen))

    const name = cardholderName(holder)
    if (!name) next.holder = copy.err_holder_empty || ''
    else if (!isCardholderName(name)) next.holder = copy.err_holder_invalid || ''

    setFieldErrors(next)
    return Object.keys(next).length === 0
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError('')
    if (!validate()) return
    if (!accept) {
      setError(copy.accept_required || '')
      return
    }
    if (!config?.tokenize_url || !config.public_key || !config.pay_card_url) {
      setError(copy.card_error || '')
      return
    }
    setBusy(true)
    try {
      const expDigits = digitsOf(exp)
      const tokenRes = await fetch(config.tokenize_url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${config.public_key}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          number: digitsOf(number),
          cvc: digitsOf(cvc),
          exp_month: expDigits.slice(0, 2),
          exp_year: expDigits.slice(2, 4),
          card_holder: holder.trim().replace(/\s+/g, ' '),
        }),
      })
      const tokenBody = await tokenRes.json()
      const cardToken = tokenBody?.data?.id
      if (!tokenRes.ok || !cardToken) {
        throw new Error(tokenBody?.error?.reason || tokenBody?.error?.type || copy.card_error)
      }
      const payRes = await fetch(config.pay_card_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ card_token: cardToken }),
      })
      const payBody = await payRes.json()
      if (!payRes.ok || !payBody.redirect) {
        throw new Error(payBody.message || copy.card_error)
      }
      window.location.href = payBody.redirect
    } catch (err) {
      const raw = err instanceof Error ? err.message : ''
      setError(raw === 'Failed to fetch' ? (copy.card_error || '') : (raw || copy.card_error || ''))
      setBusy(false)
    }
  }

  const cardError = fieldErrors.number || fieldErrors.exp || fieldErrors.cvc

  return (
    <div className="wompi-shell">
      <div className="wompi-wrap">
        {!config && !error && <p className="wompi-coins">…</p>}
        {error && !config && <p className="wompi-alert on">{error}</p>}
        {config && (
          <>
            <header className="wompi-summary">
              <p className="wompi-merchant">Meet&Live</p>
              <div className="wompi-usd">${config.usd_label} <i>USD</i></div>
              {copy.coins && <p className="wompi-coins">{copy.coins}</p>}
              {copy.one_installment && <p className="wompi-note">{copy.one_installment}</p>}
            </header>
            <form className="wompi-form" onSubmit={onSubmit} noValidate autoComplete="on">
              <h2>{copy.card_title}</h2>
              <p className={error ? 'wompi-alert on' : 'wompi-alert'}>{error}</p>
              <div className={`wompi-group${cardError ? ' has-error' : ''}`}>
                <div className="wompi-field">
                  <label htmlFor="card-number">{copy.card_number}</label>
                  <input
                    id="card-number"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="1234 1234 1234 1234"
                    value={number}
                    onChange={(event) => {
                      const digits = digitsOf(event.target.value)
                      const found = brandFor(digits)
                      const max = found ? Math.max(...found.lengths) : 16
                      const gaps = found?.gaps || [4, 8, 12]
                      setNumber(groupDigits(digits.slice(0, max), gaps))
                    }}
                  />
                </div>
                <div className="wompi-split">
                  <div className="wompi-field">
                    <label htmlFor="card-exp">{copy.card_exp}</label>
                    <input
                      id="card-exp"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      placeholder="MM / AA"
                      value={exp}
                      onChange={(event) => setExp(formatExpiry(event.target.value))}
                    />
                  </div>
                  <div className="wompi-field">
                    <label htmlFor="card-cvc">{copy.card_cvc}</label>
                    <input
                      id="card-cvc"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      placeholder={cvcLen === 4 ? '1234' : '123'}
                      value={cvc}
                      onChange={(event) => setCvc(digitsOf(event.target.value).slice(0, cvcLen))}
                    />
                  </div>
                </div>
              </div>
              {cardError && <p className="wompi-msg">{cardError}</p>}
              <div className={`wompi-field wompi-holder${fieldErrors.holder ? ' is-bad' : ''}`}>
                <label htmlFor="card-holder">{copy.card_holder}</label>
                <input
                  id="card-holder"
                  autoComplete="cc-name"
                  value={holder}
                  onChange={(event) => setHolder(event.target.value.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ '\-.]/g, ''))}
                />
                {fieldErrors.holder && <p className="wompi-msg">{fieldErrors.holder}</p>}
              </div>
              <label className="wompi-terms">
                <input type="checkbox" checked={accept} onChange={(event) => setAccept(event.target.checked)} />
                <span>{copy.accept_terms}</span>
              </label>
              <button type="submit" disabled={busy}>{busy ? copy.processing : copy.card_pay}</button>
              <p className="wompi-secure">{copy.secure_note}</p>
            </form>
            <p className="wompi-foot">
              {copy.hint_before}<a href={config.wompi_site_url || 'https://wompi.com/'} target="_blank" rel="noreferrer">Wompi</a>{copy.hint_after}
              <br />{copy.processed_by}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
